/**
 * Session Complete Modal Component
 *
 * Displays when a breathing session is completed, allowing users to save
 * their session either publicly (for leaderboard) or privately.
 * Includes a 30-second countdown for leaderboard eligibility.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface SessionCompleteModalProps {
  isOpen: boolean;
  isAuthenticated: boolean;
  onClose: () => void;
  onSavePublic: () => void;
  onSavePrivate: () => void;
  onSignUp?: () => void;
  onSignIn?: () => void;
  timeRemaining: number;
  isTimeExpired: boolean;
}

export default function SessionCompleteModal({
  isOpen,
  isAuthenticated,
  onClose,
  onSavePublic,
  onSavePrivate,
  onSignUp,
  onSignIn,
  timeRemaining,
  isTimeExpired,
}: SessionCompleteModalProps) {
  const navigate = useNavigate();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const clampedTimeRemaining = Math.max(0, Math.min(30, timeRemaining));
  const circleCircumference = 2 * Math.PI * 24;
  const progressRatio = clampedTimeRemaining / 30;
  const strokeOffset = circleCircumference * (1 - progressRatio);
  const handleSignUpClick = onSignUp ?? onClose;
  const handleSignInClick = onSignIn ?? onClose;
  const handleSkipForNow = () => {
    onClose();
    navigate('/dashboard');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
      style={{ zIndex: 2147483647 }}
    >
      {/* Backdrop (no blur to keep background sharp) */}
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

      {/* Modal Content */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-complete-title"
        className="relative mx-4 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white/95 p-6 shadow-2xl sm:mx-0 sm:rounded-2xl sm:p-8"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-3 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-500"
          aria-label="Close modal"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="h-6 w-6"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 6l12 12M6 18L18 6"
            />
          </svg>
        </button>

        {/* Celebration icon */}
        <div className="mb-4 flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 text-3xl shadow-lg sm:h-16 sm:w-16">
            🎉
          </div>
        </div>

        {/* Title */}
        <h2
          id="session-complete-title"
          className="mb-2 text-center text-2xl font-bold text-gray-900"
        >
          Session Complete!
        </h2>

        {/* Subtitle */}
        <p className="mb-4 text-center text-base font-medium text-gray-700">
          {isAuthenticated
            ? 'You finished your session — great job! How would you like to save it?'
            : 'You finished your session — great job! Create a free account to save it and keep your progress.'}
        </p>

        {/* Description */}
        {isAuthenticated ? (
          <div className="mb-6 rounded-lg bg-blue-50/50 p-4">
            <p className="text-center text-sm leading-relaxed text-gray-600">
              <span className="font-semibold text-teal-700">Public saves</span>{' '}
              count toward the global leaderboard.
              <br />
              <span className="font-semibold text-gray-700">
                Private saves
              </span>{' '}
              are only visible to you.
            </p>
          </div>
        ) : (
          <div className="mb-6 rounded-lg bg-teal-50/60 p-4">
            <p className="text-center text-sm leading-relaxed text-teal-900">
              Sign up to save this session, unlock the leaderboard, and keep
              your breathing history across devices.
            </p>
          </div>
        )}

        {/* Countdown Timer */}
        {isAuthenticated && !isTimeExpired && (
          <div className="mb-6 flex items-center justify-center gap-3">
            <svg
              className="h-12 w-12 -rotate-90 sm:h-14 sm:w-14"
              viewBox="0 0 56 56"
            >
              <circle
                cx="28"
                cy="28"
                r="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                className="text-gray-200"
              />
              <circle
                cx="28"
                cy="28"
                r="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                className="text-teal-500 transition-all duration-1000 ease-linear"
                strokeDasharray={circleCircumference}
                strokeDashoffset={strokeOffset}
              />
            </svg>
            <div className="text-center">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Time for public save
              </p>
              <p className="text-3xl font-bold tabular-nums text-teal-600">
                {clampedTimeRemaining}s
              </p>
            </div>
          </div>
        )}

        {/* Expired Message */}
        {isAuthenticated && isTimeExpired && (
          <div className="mb-6 rounded-lg bg-amber-50 p-4">
            <p className="text-center text-sm font-medium text-amber-800">
              ⏱️ Time expired — this session can only be saved privately now.
            </p>
          </div>
        )}

        {/* Buttons */}
        {isAuthenticated ? (
          <>
            <div className="space-y-3">
              {/* Public Save Button */}
              <button
                onClick={onSavePublic}
                disabled={isTimeExpired}
                className={`w-full rounded-xl px-6 py-4 text-base font-semibold text-white shadow-lg transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-teal-500/50 ${
                  isTimeExpired
                    ? 'cursor-not-allowed bg-gray-300 opacity-50'
                    : 'bg-gradient-to-r from-teal-500 to-cyan-500 hover:scale-[1.02] hover:from-teal-600 hover:to-cyan-600 hover:shadow-xl active:scale-[0.98]'
                }`}
                title={
                  isTimeExpired
                    ? 'Time expired — only private save available'
                    : 'Save session and add to public leaderboard'
                }
              >
                {isTimeExpired ? (
                  <>
                    <span className="text-white line-through">
                      Save & Share to Public Leaderboard
                    </span>
                    <span className="ml-2 text-xs text-white/80">
                      (Expired)
                    </span>
                  </>
                ) : (
                  <span className="text-white">
                    🏆 Save & Share to Public Leaderboard
                  </span>
                )}
              </button>

              {/* Private Save Button */}
              <button
                onClick={onSavePrivate}
                className="w-full rounded-xl border-2 border-gray-300 bg-white px-6 py-4 text-base font-semibold text-gray-700 shadow-sm transition-all duration-200 hover:scale-[1.02] hover:border-gray-400 hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-gray-500/50 active:scale-[0.98]"
              >
                🔒 Save Privately
              </button>
            </div>

            {/* Helper text */}
            <p className="mt-4 text-center text-xs text-gray-500">
              You can always view your private sessions in your profile.
            </p>
          </>
        ) : (
          <>
            <div className="space-y-3">
              <button
                onClick={handleSignUpClick}
                className="w-full rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-4 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:scale-[1.02] hover:from-teal-600 hover:to-cyan-600 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-teal-500/50 active:scale-[0.98]"
              >
                ✨ Sign up to save this session
              </button>
              <button
                onClick={handleSkipForNow}
                className="w-full rounded-xl border-2 border-gray-200 bg-white px-6 py-4 text-base font-semibold text-gray-600 shadow-sm transition-all duration-200 hover:scale-[1.02] hover:border-gray-300 hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-gray-400/40 active:scale-[0.98]"
              >
                Skip for now
              </button>
            </div>
            <p className="mt-4 text-center text-xs text-gray-500">
              Already have an account?{' '}
              <button
                type="button"
                onClick={handleSignInClick}
                className="font-semibold text-teal-600 transition hover:text-teal-700"
              >
                Sign in
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
